import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { CalendarDialog } from "@/components/CalendarDialog";
import { ServiceFAQ } from "@/components/ServiceFAQ";
import { CheckCircle, ArrowRight, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { RelatedServices, type RelatedService } from "@/components/RelatedServices";

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface ProcessStep {
  step: string;
  title: string;
  text: string;
}

interface ServicePageTemplateProps {
  title: string;
  subtitle: string;
  metaTitle: string;
  metaDescription: string;
  canonicalUrl: string;
  badgeText: string;
  icon: LucideIcon;
  introText: string;
  features: Feature[];
  benefits: string[];
  ctaHeading: string;
  ctaText: string;
  extraContent?: React.ReactNode;
  faqs?: { question: string; answer: string }[];
  relatedServices?: RelatedService[];
  bestFor?: string;
  process?: ProcessStep[];
}

export function ServicePageTemplate({
  title,
  subtitle,
  metaTitle,
  metaDescription,
  canonicalUrl,
  badgeText,
  icon: Icon,
  introText,
  features,
  benefits,
  ctaHeading,
  ctaText,
  extraContent,
  faqs,
  relatedServices,
  bestFor,
  process,
}: ServicePageTemplateProps) {
  const faqSchema = faqs && faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  } : null;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      {/* Hero */}
      <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-14 sm:pb-18 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-[hsl(100_25%_97%)] to-[hsl(98_30%_95%/0.6)]" />
        <div className="absolute inset-0 bg-gradient-hero-glow pointer-events-none" />
        <div className="absolute inset-0 grid-pattern opacity-30" />

        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="section-badge mb-4">
              <span className="text-xs">{badgeText}</span>
            </div>
            <h1 className="text-[1.875rem] sm:text-[2.25rem] lg:text-[3rem] font-extrabold text-foreground leading-[1.12] mb-4 tracking-[-0.025em]">
              {title}
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-[1.7] max-w-2xl mx-auto mb-6">
              {subtitle}
            </p>
            {bestFor && (
              <p className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-primary/10 border border-primary/20 text-[0.8125rem] sm:text-sm text-foreground">
                <span className="font-bold text-primary">Best for:</span>
                <span>{bestFor}</span>
              </p>
            )}
            <div className="flex justify-center">
              <CalendarDialog
                buttonVariant="default"
                buttonSize="lg"
                buttonClassName="h-12 text-sm font-bold rounded-xl"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-muted relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-wide relative z-10">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex items-start gap-4"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                <Icon className="w-6 h-6 text-primary" />
              </div>
              <p className="text-foreground text-base sm:text-lg leading-[1.75]">
                {introText}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-25" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8 sm:mb-10"
          >
            <h2 className="heading-section text-foreground mb-3">
              What's <span className="text-gradient">Included</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="card-lift bg-card rounded-xl p-5 sm:p-6 border border-border/60 shadow-card"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-base font-bold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-[0.9375rem] leading-[1.65]">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-muted relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-wide relative z-10">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-8"
            >
              <h2 className="heading-section text-foreground mb-3">
                Key <span className="text-gradient">Benefits</span>
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-3">
              {benefits.map((benefit, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  className="flex items-start gap-3 bg-card rounded-lg p-4 border border-border/50"
                >
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground text-[0.9375rem] leading-[1.5]">
                    {benefit}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process / Timeline */}
      {process && process.length > 0 && (
        <section className="section-padding bg-background relative overflow-hidden">
          <div className="absolute inset-0 grid-pattern opacity-25" />
          <div className="container-wide relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-8 sm:mb-10"
            >
              <h2 className="heading-section text-foreground mb-3">
                What Happens <span className="text-gradient">After You Sign Up</span>
              </h2>
              <p className="text-muted-foreground text-base max-w-xl mx-auto">
                A simple, proven 4-step rollout — no mystery, no fluff.
              </p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {process.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.4 }}
                  className="relative card-lift bg-card rounded-xl p-5 border border-border/60 shadow-card"
                >
                  <span className="absolute top-3 right-4 text-3xl font-bold text-primary/10 leading-none">
                    {item.step}
                  </span>
                  <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center mb-3 font-bold text-sm">
                    {item.step}
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-1.5">{item.title}</h3>
                  <p className="text-muted-foreground text-[0.875rem] leading-[1.6]">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {extraContent}

      {faqs && faqs.length > 0 && <ServiceFAQ faqs={faqs} />}

      {relatedServices && relatedServices.length > 0 && (
        <RelatedServices services={relatedServices} />
      )}
    </>
  );
}
